import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type {
  UpstreamCatalogModel,
  UpstreamConfig,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useFundingBatches } from '../api/funding.ts';
import { usePricing } from '../api/pricing.ts';
import { useUpstreamAccountsList } from '../api/useUpstreamAccounts.ts';
import {
  useDeleteUpstream,
  useUpdateUpstream,
  useUpstream,
  useUpstreamCatalog,
  useUpstreamRetirementImpact,
} from '../api/useUpstreams.ts';
import { UpstreamModelSkuTable } from '../components/upstream-model-skus/UpstreamModelSkuTable.tsx';
import { SupplyAccessDrawers } from '../components/upstream-supply-access/SupplyAccessDrawers.tsx';
import type { SupplyAccessDrawerId } from '../components/upstream-supply-access/SupplyAccessDrawers.tsx';
import { SupplyAccessSection } from '../components/upstream-supply-access/SupplyAccessSection.tsx';
import {
  formatCnyCents,
  formatUsdCentsAsCnyBalance,
} from '../components/formatters.ts';
import { InlineTextField } from '../components/InlineTextField.tsx';
import { ModelDiscoveryConfigField } from '../components/ModelDiscoveryConfigField.tsx';
import { Modal } from '../components/Modal.tsx';
import { ProbeHistoryCard } from '../components/ProbeHistoryCard.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  Dialog,
  Input,
  PageHeader,
  Select,
} from '../ui/index.ts';

/**
 * `/supply/upstreams/:id` — the supply domain, at two grains (epic #1327).
 *
 *   Section 1 · Supply access — per upstream namespace: can it carry
 *     traffic? Four readiness facts, each unresolved one click-through to
 *     the drawer that fixes it.
 *   Section 2 · Upstream model SKUs — per model: what does the namespace
 *     expose, how is it classified and probed, what does it cost to buy.
 *
 * Configuration and probe history stay page-level because they are facts
 * about the upstream itself, not about any one namespace. Account, channel,
 * and funding management moved into drawers: they are edits, and the page
 * reads better as a statement of readiness than as six panes of forms.
 *
 * Field edits are inline: blur or Enter fires a PATCH, and the displayed
 * value only changes once React Query re-reads. Sluggish on slow links, but
 * the page never shows a value the gateway has not accepted.
 */

export function UpstreamDetail(): JSX.Element {
  const t = useT();
  const params = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const upstreamId = params.id ?? '';
  const upstream = useUpstream(upstreamId);
  const catalog = useUpstreamCatalog();
  const openDrawer = drawerForTabParam(searchParams.get('tab'));
  const focusAccountId = searchParams.get('accountId');
  const focusKeyBucketId = searchParams.get('keyBucketId');
  const focusAddChannelId = searchParams.get('addChannelId');

  /**
   * The URL owns which drawer is open, rather than local state, for two
   * reasons: links that arrive from elsewhere in the console still carry
   * `?tab=…` (the supply workbench's own channel-catalog link is one), and a
   * drawer an operator is looking at stays shareable. Closing also drops the
   * focus parameters — they scroll a specific account or key into view, and
   * replaying them on the next open would re-target a surface the operator
   * has since navigated away from.
   */
  const setOpenDrawer = (next: SupplyAccessDrawerId | null): void => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      if (next === null) {
        params.delete('tab');
        params.delete('accountId');
        params.delete('keyBucketId');
        params.delete('addChannelId');
      } else {
        params.set('tab', next);
      }
      return params;
    }, { replace: true });
  };

  const attached = useMemo<UpstreamCatalogModel[]>(() => {
    const entries = catalog.data?.entries ?? [];
    const match = entries.find((e) => e.upstream.id === upstreamId);
    return match?.models ?? [];
  }, [catalog.data, upstreamId]);

  if (upstream.isPending) {
    return (
      <section className="flex flex-col gap-6" data-testid="upstream-detail-loading">
        <div className="h-8 w-64 animate-pulse rounded bg-card" aria-hidden />
        <div className="h-32 animate-pulse rounded-lg bg-card" aria-hidden />
        <div className="h-48 animate-pulse rounded-lg bg-card" aria-hidden />
        <span className="sr-only">{t('common.loading')}</span>
      </section>
    );
  }

  if (upstream.isError) {
    return (
      <section
        className="flex flex-col gap-4 rounded-lg border border-bad-line bg-bad-dim p-6"
        role="alert"
        data-testid="upstream-detail-error"
      >
        <h2 className="m-0 text-section text-bad-bright">{t('upstream_detail.error.title')}</h2>
        <p className="m-0 text-body text-bad-bright/80">
          {upstream.error instanceof OpsApiError
            ? `${upstream.error.code}: ${upstream.error.message}`
            : t('common.network_error')}
        </p>
        <div className="flex items-center gap-3">
          <Button size="sm" onClick={() => void upstream.refetch()}>
            {t('common.retry')}
          </Button>
          <Link
            to="/supply/upstreams"
            className="text-label text-paper-mid transition-colors duration-120 hover:text-paper"
          >
            {t('upstream_detail.back')}
          </Link>
        </div>
      </section>
    );
  }

  return (
    <UpstreamDetailReady
      upstream={upstream.data}
      attachedModels={attached}
      openDrawer={openDrawer}
      onOpenDrawer={setOpenDrawer}
      focusAccountId={focusAccountId}
      focusKeyBucketId={focusKeyBucketId}
      focusAddChannelId={focusAddChannelId}
    />
  );
}

interface ReadyProps {
  readonly upstream: UpstreamConfig;
  readonly attachedModels: readonly UpstreamCatalogModel[];
  readonly openDrawer: SupplyAccessDrawerId | null;
  readonly onOpenDrawer: (next: SupplyAccessDrawerId | null) => void;
  readonly focusAccountId: string | null;
  readonly focusKeyBucketId: string | null;
  readonly focusAddChannelId: string | null;
}

/**
 * Existing deep links still name a tab (`?tab=accounts&keyBucketId=…` from
 * the tenant-routing dialog, the audit log, and shared operator URLs). The
 * tabs are gone, so the parameter now selects a drawer — the surface that
 * inherited each pane's writes. `config`, `routing`, and `probe` map to
 * nothing because their content is on the page already; a stale link to one
 * lands on the page it was always about.
 */
function drawerForTabParam(raw: string | null): SupplyAccessDrawerId | null {
  switch (raw) {
    case 'accounts':
      return 'accounts';
    case 'channels':
      return 'channels';
    case 'funding':
      return 'funding';
    default:
      return null;
  }
}

function formatDiagnosticSource(no: number): string {
  return `S${Math.max(0, Math.floor(no)).toString().padStart(3, '0')}`;
}

function UpstreamDetailReady({
  upstream,
  attachedModels,
  openDrawer,
  onOpenDrawer,
  focusAccountId,
  focusKeyBucketId,
  focusAddChannelId,
}: ReadyProps): JSX.Element {
  const t = useT();
  const variant = upstream.enabled ? 'ok' : 'warning';
  const statusLabel = upstream.enabled
    ? t('upstream_detail.chip.enabled')
    : t('upstream_detail.chip.disabled');

  // Summary stats data
  const accountsQ = useUpstreamAccountsList(upstream.id);
  const fundingQ = useFundingBatches(upstream.id);

  const accountCount = accountsQ.data?.length ?? 0;
  const fundingRemainingCnyLabel = useMemo(() => {
    const batches = fundingQ.data;
    if (batches === undefined || batches.length === 0) return null;
    let totalCnyCents = 0;
    for (const b of batches) {
      if (!b.is_depleted) {
        const remaining = b.cny_paid_cents - b.cny_consumed_cents;
        // Overspent positive batches must not pull the total down;
        // negative-capacity corrections must, or the book remaining
        // would ignore the very adjustment they exist to record.
        totalCnyCents += b.cny_paid_cents < 0 ? remaining : Math.max(0, remaining);
      }
    }
    return formatCnyCents(totalCnyCents, totalCnyCents < 0);
  }, [fundingQ.data]);

  return (
    <section className="flex flex-col gap-6" data-testid="upstream-detail-page">
      {/* Breadcrumb + Header */}
      <div>
        <nav aria-label="breadcrumb" className="mb-2 flex items-center gap-2 text-dense text-paper-low">
          <Link to="/supply/upstreams" className="transition-colors duration-120 hover:text-paper">
            {t('upstream_detail.breadcrumb.upstreams')}
          </Link>
          <span aria-hidden>/</span>
          <span className="text-paper-mid">{upstream.name}</span>
        </nav>
        <PageHeader title={<span data-testid="upstream-detail-title">{upstream.name}</span>} />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <StatusChip
            variant={variant}
            label={statusLabel}
            testId="upstream-detail-status"
          />
          <span className="rounded-full border border-line px-2.5 py-0.5 font-mono text-micro text-paper-low">
            {upstream.kind}
          </span>
        </div>

        {/* Summary Stats Bar */}
        <div
          className="mt-6 grid grid-cols-2 gap-y-4 rounded-lg border border-line-soft bg-card p-4 sm:grid-cols-4"
          data-testid="upstream-summary-stats"
        >
          <div className="flex flex-col px-4">
            <span className="mb-1 text-label text-paper-low">
              {t('upstream_detail.summary.source')}
            </span>
            <span className="font-mono text-stat text-paper">
              {formatDiagnosticSource(upstream.diagnostic_source_no)}
            </span>
          </div>
          <div className="flex flex-col px-4 sm:border-l sm:border-line-soft">
            <span className="mb-1 text-label text-paper-low">
              {t('upstream_detail.summary.endpoint')}
            </span>
            <span className="truncate font-mono text-body text-paper" title={upstream.endpoint_url}>
              {upstream.endpoint_url}
            </span>
          </div>
          <div className="flex flex-col px-4 sm:border-l sm:border-line-soft">
            <span className="mb-1 text-label text-paper-low">
              {t('upstream_detail.summary.accounts')}
            </span>
            <div className="flex items-baseline gap-1">
              <span className="font-mono text-stat text-paper">{accountCount}</span>
              {t('upstream_detail.summary.accounts_unit').length > 0 ? (
                <span className="text-dense text-paper-low">
                  {t('upstream_detail.summary.accounts_unit')}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col px-4 sm:border-l sm:border-line-soft">
            <span className="mb-1 text-label text-paper-low">
              {t('upstream_detail.summary.funding')}
            </span>
            <div className="flex items-baseline gap-1">
              {fundingRemainingCnyLabel !== null ? (
                <>
                  <span className="font-mono text-stat text-paper" data-testid="upstream-detail-funding-remaining">
                    {fundingRemainingCnyLabel}
                  </span>
                  <span className="text-dense text-paper-low">
                    {t('upstream_detail.summary.funding_remaining')}
                  </span>
                </>
              ) : (
                <span className="text-body text-paper-low">
                  {t('upstream_detail.summary.funding_none')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/*
          Drawer entries sit under the summary rather than beside the
          readiness facts: the facts open a drawer *because something is
          wrong*, while these are the "I know what I want to change" path,
          including funding, which no readiness fact covers.
        */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => onOpenDrawer('accounts')} data-testid="upstream-open-accounts-drawer">
            {t('upstream_supply_access.action.accounts')}
          </Button>
          <Button size="sm" onClick={() => onOpenDrawer('channels')} data-testid="upstream-open-channels-drawer">
            {t('upstream_supply_access.action.channels')}
          </Button>
          <Button size="sm" onClick={() => onOpenDrawer('funding')} data-testid="upstream-open-funding-drawer">
            {t('upstream_supply_access.action.funding')}
          </Button>
        </div>
      </div>

      {/* Section 1 — supply access, per upstream namespace. */}
      <SupplyAccessSection upstreamId={upstream.id} onOpenDrawer={onOpenDrawer} />

      {/* Section 2 — upstream model SKUs, per model (PR-5). */}
      <UpstreamModelSkuTable upstreamId={upstream.id} modelDiscovery={upstream.model_discovery} />

      <ConfigurationCard upstream={upstream} attached={attachedModels} />
      <ProbeHistoryCard upstreamId={upstream.id} />
      <DangerZone upstreamId={upstream.id} upstreamName={upstream.name} />

      <SupplyAccessDrawers
        upstreamId={upstream.id}
        open={openDrawer}
        onOpenChange={onOpenDrawer}
        accounts={accountsQ.data ?? []}
        focusAccountId={focusAccountId}
        focusKeyBucketId={focusKeyBucketId}
        focusAddChannelId={focusAddChannelId}
      />
    </section>
  );
}

interface ConfigurationCardProps {
  readonly upstream: UpstreamConfig;
  readonly attached: readonly UpstreamCatalogModel[];
}

function ConfigurationCard({ upstream, attached }: ConfigurationCardProps): JSX.Element {
  const t = useT();
  const update = useUpdateUpstream(upstream.id);
  const toast = useToast();
  const [mutationError, setMutationError] = useState<string | null>(null);

  async function handlePatch(body: Parameters<typeof update.mutateAsync>[0]): Promise<void> {
    setMutationError(null);
    try {
      await update.mutateAsync(body);
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setMutationError(message);
      toast.error(message);
    }
  }

  return (
    <article
      data-testid="upstream-config-card"
      className="flex flex-col gap-6 rounded-lg border border-line-soft bg-card p-6"
    >
      <header className="flex items-center justify-between">
        <h3 className="m-0 text-section text-paper">
          {t('upstream_detail.config.title')}
        </h3>
        {update.isPending ? (
          <span className="text-label text-paper-low">
            {t('common.saving')}
          </span>
        ) : null}
      </header>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <InlineTextField
          label={t('upstream_detail.config.display_name')}
          initial={upstream.name}
          testId="upstream-config-name"
          onSubmit={(value) => handlePatch({ name: value })}
        />
        <InlineTextField
          label={t('upstream_detail.config.endpoint_url')}
          initial={upstream.endpoint_url}
          testId="upstream-config-endpoint-url"
          mono
          onSubmit={(value) => handlePatch({ endpoint_url: value })}
        />
        <SecretPreviewField
          upstream={upstream}
          pending={update.isPending}
          onRotate={async (value) => {
            await handlePatch({ auth_secret_ref: value });
          }}
        />
        <ModelDiscoveryConfigField upstream={upstream} />
        <RegionField
          region={upstream.region ?? 'jp'}
          pending={update.isPending}
          onChange={(region) => {
            void handlePatch({ region });
          }}
        />
        <PinnedEgressField
          pinnedId={upstream.pinned_egress_link_id ?? null}
          pending={update.isPending}
          onChange={(pinned) => {
            void handlePatch({ pinned_egress_link_id: pinned });
          }}
        />
        <SessionPoolField
          enabled={upstream.session_pool === true}
          pending={update.isPending}
          onTogglePatch={async (nextEnabled) => {
            await handlePatch({ session_pool: nextEnabled });
          }}
        />
        <CookiePassthroughField
          enabled={upstream.allow_cookie_passthrough}
          pending={update.isPending}
          onTogglePatch={async (nextEnabled) => {
            await handlePatch({ allow_cookie_passthrough: nextEnabled });
          }}
        />
        <RequestGzipRatioField
          ratio={upstream.request_gzip_ratio ?? 0}
          pending={update.isPending}
          onSubmit={async (next) => {
            await handlePatch({ request_gzip_ratio: next });
          }}
        />
        <MaintenanceToggleField
          upstream={upstream}
          attached={attached}
          pending={update.isPending}
          onTogglePatch={async (nextEnabled) => {
            await handlePatch({ enabled: nextEnabled });
          }}
        />
      </div>
      {mutationError !== null ? (
        <p
          className="m-0 text-label text-bad"
          role="alert"
          data-testid="upstream-config-error"
        >
          {mutationError}
        </p>
      ) : null}
    </article>
  );
}

function RegionField({
  region,
  pending,
  onChange,
}: {
  readonly region: string;
  readonly pending: boolean;
  readonly onChange: (region: string) => void;
}): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-2 rounded-lg bg-well p-4" data-testid="upstream-config-region">
      <span className="text-label text-paper-mid">{t('upstream_detail.config.region')}</span>
      <Select
        value={region}
        onValueChange={onChange}
        disabled={pending}
        testId="upstream-config-region-select"
        options={[
          { value: 'cn', label: 'cn' },
          { value: 'jp', label: 'jp' },
          { value: 'us', label: 'us' },
        ]}
      />
      <span className="text-dense text-paper-low">{t('upstream_detail.config.region.hint')}</span>
    </div>
  );
}

function PinnedEgressField({
  pinnedId,
  pending,
  onChange,
}: {
  readonly pinnedId: string | null;
  readonly pending: boolean;
  readonly onChange: (pinned: string | null) => void;
}): JSX.Element {
  const t = useT();
  const [draft, setDraft] = useState(pinnedId ?? '');
  return (
    <div className="flex flex-col gap-2 rounded-lg bg-well p-4" data-testid="upstream-config-pin">
      <div className="flex items-center gap-2">
        <span className="text-label text-paper-mid">{t('upstream_detail.config.pin')}</span>
        {pinnedId !== null ? (
          <Chip tone="warn" lamp={false} testId="upstream-config-pin-badge">
            {t('upstream_detail.config.pin.badge')}
          </Chip>
        ) : null}
      </div>
      {/* Free-text id. Existence is not validated here — runtime warns
          and ignores an unknown pin. A dropdown of live links is a later
          enhancement. */}
      <Input
        value={draft}
        disabled={pending}
        placeholder={t('upstream_detail.config.pin.empty')}
        data-testid="upstream-config-pin-input"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          const next = draft.trim();
          const normalized = next.length === 0 ? null : next;
          if (normalized !== pinnedId) onChange(normalized);
        }}
      />
      <span className="text-dense text-paper-low">{t('upstream_detail.config.pin.hint')}</span>
    </div>
  );
}

/**
 * The upstream-level credential. Preview-only for reads, PLUS a write-only
 * rotate input, because before this input existed no console surface could set
 * `upstream_configs.auth_secret_ref` at all — the wizard never asks (#1883
 * operator-journey closure).
 *
 * It is the asynchronous task lane's FALLBACK credential, not the one it
 * normally polls with: `readGenerationTaskUpstream` resolves the key bucket the
 * task row names first and reads this column only when that bucket holds
 * nothing (#1981). So a wizard-created upstream whose credential lives on a key
 * bucket polls fine without this field ever being set — and one with neither is
 * refused before the dial (`credential.ts`), rather than dialling `Bearer ` and
 * being told the upstream is down.
 */
function SecretPreviewField({
  upstream,
  pending,
  onRotate,
}: {
  readonly upstream: UpstreamConfig;
  readonly pending: boolean;
  readonly onRotate: (value: string) => Promise<void>;
}): JSX.Element {
  const t = useT();
  const [draft, setDraft] = useState('');
  return (
    <div
      className="flex flex-col gap-2 rounded-lg bg-well p-4"
      data-testid="upstream-config-secret-preview"
    >
      <span className="text-label text-paper-mid">
        {t('upstream_detail.config.auth_secret')}
      </span>
      <span className="font-mono text-body text-paper">
        {upstream.has_auth_secret
          ? upstream.auth_secret_preview ?? t('upstream_detail.config.auth_secret.write_only')
          : t('upstream_detail.config.auth_secret.empty')}
      </span>
      <span className="text-dense text-paper-low">
        {t('upstream_detail.config.auth_secret.hint')}
      </span>
      <form
        className="flex items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const value = draft.trim();
          if (value.length === 0 || pending) return;
          void onRotate(value).then(() => setDraft(''));
        }}
      >
        <Input
          type="password"
          autoComplete="new-password"
          mono
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t('upstream_detail.config.auth_secret.rotate_placeholder')}
          data-testid="upstream-config-secret-rotate-input"
          disabled={pending}
          aria-label={t('upstream_detail.config.auth_secret.rotate')}
        />
        <Button
          type="submit"
          variant="secondary"
          size="sm"
          disabled={pending || draft.trim().length === 0}
          data-testid="upstream-config-secret-rotate-submit"
        >
          {t('upstream_detail.config.auth_secret.rotate')}
        </Button>
      </form>
    </div>
  );
}

interface SessionPoolFieldProps {
  readonly enabled: boolean;
  readonly pending: boolean;
  readonly onTogglePatch: (nextEnabled: boolean) => Promise<void>;
}

function SessionPoolField({
  enabled,
  pending,
  onTogglePatch,
}: SessionPoolFieldProps): JSX.Element {
  const t = useT();
  return (
    <label
      className="flex flex-col gap-2 rounded-lg bg-well p-4"
      data-testid="upstream-config-session-pool"
    >
      <span className="text-label text-paper-mid">
        {t('upstream_detail.config.session_pool')}
      </span>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={enabled}
          disabled={pending}
          onChange={(e) => {
            void onTogglePatch(e.currentTarget.checked);
          }}
          data-testid="upstream-config-session-pool-toggle"
          className="mt-1 h-4 w-4 shrink-0 accent-signal disabled:opacity-50"
        />
        <div className="flex flex-col gap-1">
          <span className="text-body text-paper">
            {enabled
              ? t('upstream_detail.config.session_pool.state.enabled')
              : t('upstream_detail.config.session_pool.state.disabled')}
          </span>
          <span className="text-dense text-paper-low">
            {t('upstream_detail.config.session_pool.hint')}
          </span>
        </div>
      </div>
    </label>
  );
}

function RequestGzipRatioField({
  ratio,
  pending,
  onSubmit,
}: {
  readonly ratio: number;
  readonly pending: boolean;
  readonly onSubmit: (next: number) => Promise<void>;
}): JSX.Element {
  const t = useT();
  const [value, setValue] = useState(String(ratio));
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setValue(String(ratio));
    setDirty(false);
  }, [ratio]);

  async function commit(): Promise<void> {
    if (!dirty || pending) return;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      setValue(String(ratio));
      setDirty(false);
      return;
    }
    await onSubmit(parsed);
    setDirty(false);
  }

  return (
    <label className="flex flex-col gap-2 rounded-lg bg-well p-4" data-testid="upstream-config-request-gzip-ratio">
      <span className="text-label text-paper-mid">
        {t('upstream_detail.config.request_gzip_ratio')}
      </span>
      <Input
        type="number"
        min={0}
        max={1}
        step="0.1"
        value={value}
        disabled={pending}
        onChange={(e) => {
          setValue(e.currentTarget.value);
          setDirty(true);
        }}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          }
        }}
        data-testid="upstream-config-request-gzip-ratio-input"
        mono
      />
      <span className="text-dense text-paper-low">
        {t('upstream_detail.config.request_gzip_ratio.hint')}
      </span>
    </label>
  );
}

interface CookiePassthroughFieldProps {
  readonly enabled: boolean;
  readonly pending: boolean;
  readonly onTogglePatch: (nextEnabled: boolean) => Promise<void>;
}

function CookiePassthroughField({
  enabled,
  pending,
  onTogglePatch,
}: CookiePassthroughFieldProps): JSX.Element {
  const t = useT();
  return (
    <label
      className="flex flex-col gap-2 rounded-lg bg-well p-4"
      data-testid="upstream-config-cookie-passthrough"
    >
      <span className="text-label text-paper-mid">
        {t('upstream_detail.config.cookie_passthrough')}
      </span>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={enabled}
          disabled={pending}
          onChange={(e) => {
            void onTogglePatch(e.currentTarget.checked);
          }}
          data-testid="upstream-config-cookie-passthrough-toggle"
          className="mt-1 h-4 w-4 shrink-0 accent-signal disabled:opacity-50"
        />
        <div className="flex flex-col gap-1">
          <span className="text-body text-paper">
            {enabled
              ? t('upstream_detail.config.cookie_passthrough.state.enabled')
              : t('upstream_detail.config.cookie_passthrough.state.disabled')}
          </span>
          <span className="text-dense text-paper-low">
            {t('upstream_detail.config.cookie_passthrough.hint')}
          </span>
        </div>
      </div>
    </label>
  );
}

interface DangerZoneProps {
  readonly upstreamId: string;
  readonly upstreamName: string;
}

function DangerZone({ upstreamId, upstreamName }: DangerZoneProps): JSX.Element {
  const t = useT();
  const del = useDeleteUpstream(upstreamId);
  const toast = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only fetch the impact snapshot while the dialog is open. Reset the
  // acknowledgement + error when the dialog re-opens so prior state
  // doesn't bleed through.
  const impactQ = useUpstreamRetirementImpact(upstreamId, { enabled: confirmOpen });

  async function handleConfirm(): Promise<void> {
    setError(null);
    try {
      await del.mutateAsync();
      setConfirmOpen(false);
      setAcknowledged(false);
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
      // Force the operator to re-tick after a failed delete — the
      // error banner alone isn't enough; without this reset the
      // Delete button stays enabled and a second click fires a retry
      // without a fresh acknowledgement.
      setAcknowledged(false);
    }
  }

  function closeDialog(): void {
    if (del.isPending) return;
    setConfirmOpen(false);
    setAcknowledged(false);
    setError(null);
  }

  // Delete is gated by three conditions: (a) impact snapshot loaded
  // (we refuse to let the operator act without seeing the blast
  // radius); (b) operator ticked the acknowledgement box; (c) no
  // in-flight delete mutation already.
  const impactReady = impactQ.data !== undefined;
  const deleteEnabled = impactReady && acknowledged && !del.isPending;

  return (
    <article
      data-testid="upstream-danger-zone"
      className="flex flex-col gap-4 rounded-lg border border-bad-line bg-card p-6"
    >
      <header className="flex items-center gap-3">
        <h3 className="m-0 text-section text-bad">
          {t('upstream_detail.danger.title')}
        </h3>
      </header>
      <p className="m-0 text-body text-paper-low">{t('upstream_detail.danger.body')}</p>
      <div>
        <Button
          variant="danger"
          onClick={() => {
            setConfirmOpen(true);
            setAcknowledged(false);
            setError(null);
          }}
          data-testid="upstream-delete"
        >
          {t('upstream_detail.danger.delete')}
        </Button>
      </div>
      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
        title={t('upstream_detail.delete_modal.title', { name: upstreamName })}
        description={t('upstream_detail.delete_modal.body')}
        size="lg"
        closeLabel={t('common.close')}
        testId="upstream-delete-dialog"
        footer={
          <>
            <Button variant="ghost" onClick={closeDialog} disabled={del.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => void handleConfirm()}
              disabled={!deleteEnabled}
              data-testid="upstream-delete-confirm"
            >
              {del.isPending ? t('common.deleting') : t('common.delete')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-5">
          {impactQ.isPending ? (
            <p
              className="m-0 text-dense text-paper-low"
              data-testid="upstream-retirement-loading"
              role="status"
            >
              {t('upstream_detail.retirement.loading')}
            </p>
          ) : null}

          {impactQ.isError ? (
            <div
              className="flex flex-col gap-2 rounded-lg border border-bad-line bg-bad-dim p-4 text-dense text-bad-bright"
              role="alert"
              data-testid="upstream-retirement-error"
            >
              <span>{t('upstream_detail.retirement.load_error')}</span>
              <div>
                <Button
                  size="sm"
                  onClick={() => void impactQ.refetch()}
                  data-testid="upstream-retirement-retry"
                >
                  {t('upstream_detail.retirement.retry')}
                </Button>
              </div>
            </div>
          ) : null}

          {impactQ.data !== undefined ? (
            <RetirementImpactSummary impact={impactQ.data} />
          ) : null}

          {impactReady ? (
            <label
              className="flex items-start gap-3 rounded-lg bg-well p-4 text-body text-paper"
              data-testid="upstream-retirement-ack-label"
            >
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.currentTarget.checked)}
                data-testid="upstream-retirement-ack"
                className="mt-1 h-4 w-4 shrink-0 accent-signal"
              />
              <span>{t('upstream_detail.retirement.ack')}</span>
            </label>
          ) : null}

          {error !== null ? (
            <p
              className="m-0 text-label text-bad"
              role="alert"
              data-testid="upstream-delete-error"
            >
              {error}
            </p>
          ) : null}
        </div>
      </Dialog>
    </article>
  );
}

/**
 * Three-card summary of the retirement-impact payload. Extracted so
 * the DangerZone render stays focused on dialog lifecycle.
 */
function RetirementImpactSummary({
  impact,
}: {
  readonly impact: {
    readonly uniquely_served_models: ReadonlyArray<{
      readonly vendor: string;
      readonly channel: string;
      readonly model: string;
      readonly protocol: string;
    }>;
    readonly total_attached_models: number;
    readonly traffic_share_24h: {
      readonly this_upstream_requests: number;
      readonly total_requests: number;
      readonly percentage: number;
    };
    readonly active_funding_batches: {
      readonly count: number;
      readonly remaining_usd_cents: number;
    };
  };
}): JSX.Element {
  const t = useT();
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const uniqueCount = impact.uniquely_served_models.length;
  return (
    <div
      className="flex flex-col gap-3"
      data-testid="upstream-retirement-summary"
    >
      <section
        className={[
          'flex flex-col gap-2 rounded-lg p-4 text-dense',
          uniqueCount > 0
            ? 'border border-bad-line bg-bad-dim text-bad-bright'
            : 'bg-well text-paper-mid',
        ].join(' ')}
        data-testid="upstream-retirement-uniquely-served"
      >
        <h3 className="m-0 text-label font-medium">
          {t('upstream_detail.retirement.uniquely_served.title')}
        </h3>
        {uniqueCount > 0 ? (
          <>
            <p className="m-0">
              {t('upstream_detail.retirement.uniquely_served.body', {
                count: String(uniqueCount),
              })}
            </p>
            <ul className="list-disc space-y-1 pl-5 font-mono">
              {impact.uniquely_served_models.map((m) => (
                <li
                  key={`${m.vendor}/${m.channel}/${m.model}/${m.protocol}`}
                  data-testid="upstream-retirement-uniquely-served-row"
                >
                  {m.vendor}/{m.channel}/{m.model} · {m.protocol}
                </li>
              ))}
            </ul>
            <p className="m-0 text-label text-paper-low">
              {t('upstream_detail.retirement.total_attached_suffix', {
                total: String(impact.total_attached_models),
                unique: String(uniqueCount),
              })}
            </p>
          </>
        ) : (
          <p className="m-0">{t('upstream_detail.retirement.uniquely_served.none')}</p>
        )}
      </section>

      <section
        className="flex flex-col gap-2 rounded-lg bg-well p-4 text-dense text-paper-mid"
        data-testid="upstream-retirement-funding"
      >
        <h3 className="m-0 text-label font-medium text-paper">
          {t('upstream_detail.retirement.funding.title')}
        </h3>
        {impact.active_funding_batches.count > 0
          || impact.active_funding_batches.remaining_usd_cents !== 0 ? (
          <p className="m-0">
            {t('upstream_detail.retirement.funding.body', {
              count: String(impact.active_funding_batches.count),
              remaining: formatUsdCentsAsCnyBalance(
                impact.active_funding_batches.remaining_usd_cents,
                cnyPerUsd,
              ),
            })}
          </p>
        ) : (
          <p className="m-0">{t('upstream_detail.retirement.funding.none')}</p>
        )}
      </section>

      <section
        className="flex flex-col gap-2 rounded-lg bg-well p-4 text-dense text-paper-mid"
        data-testid="upstream-retirement-traffic"
      >
        <h3 className="m-0 text-label font-medium text-paper">
          {t('upstream_detail.retirement.traffic.title')}
        </h3>
        {impact.traffic_share_24h.total_requests > 0 ? (
          <p className="m-0">
            {t('upstream_detail.retirement.traffic.body', {
              percentage: impact.traffic_share_24h.percentage.toFixed(1),
              handled: String(impact.traffic_share_24h.this_upstream_requests),
              total: String(impact.traffic_share_24h.total_requests),
            })}
          </p>
        ) : (
          <p className="m-0">{t('upstream_detail.retirement.traffic.no_traffic')}</p>
        )}
      </section>
    </div>
  );
}

// ─── Maintenance toggle (Epic #109 / P2.11) ─────────────────────────────
//
// Replaces the raw enabled checkbox on the Configuration card with a
// guarded flow. Going INTO maintenance (currently enabled → disable)
// opens an impact-preview modal that lists the attached triples plus a
// type-to-confirm gate ("maintenance"). Coming OUT (disabled → enable)
// is low-risk and uses a lighter single-click confirm modal.
//
// The gateway selector already filters `enabled = 1` at the
// tenant-request routing sites (v1-messages candidate query, policy
// selector fallback), so flipping the column cuts off live traffic
// immediately. No schema change, no new endpoint.

interface MaintenanceToggleFieldProps {
  readonly upstream: UpstreamConfig;
  readonly attached: readonly UpstreamCatalogModel[];
  readonly pending: boolean;
  readonly onTogglePatch: (nextEnabled: boolean) => Promise<void>;
}

function MaintenanceToggleField({
  upstream,
  attached,
  pending,
  onTogglePatch,
}: MaintenanceToggleFieldProps): JSX.Element {
  const t = useT();
  const [draftOpen, setDraftOpen] = useState<false | 'enter' | 'resume'>(false);
  const [typed, setTyped] = useState('');
  const [modalError, setModalError] = useState<string | null>(null);

  function openModal(direction: 'enter' | 'resume'): void {
    setDraftOpen(direction);
    setTyped('');
    setModalError(null);
  }

  function closeModal(): void {
    if (pending) return;
    setDraftOpen(false);
    setTyped('');
    setModalError(null);
  }

  async function handleConfirm(): Promise<void> {
    if (draftOpen === false) return;
    setModalError(null);
    if (draftOpen === 'enter') {
      // Type-to-confirm only when entering maintenance (the dangerous
      // direction). The literal matches the i18n key so operators who
      // read the modal body in either locale see the same token.
      const CONFIRM_TOKEN = 'maintenance';
      if (typed.trim() !== CONFIRM_TOKEN) {
        setModalError(t('upstream_detail.maintenance.confirm.type_mismatch'));
        return;
      }
    }
    try {
      await onTogglePatch(draftOpen === 'resume');
      setDraftOpen(false);
      setTyped('');
    } catch {
      // onTogglePatch surfaces errors via the caller's toast + mutation
      // error state; keep the modal open so the operator can retry.
    }
  }

  return (
    <div className="flex flex-col gap-2" data-testid="upstream-config-maintenance">
      <span className="text-label text-paper-mid">
        {t('upstream_detail.maintenance.label')}
      </span>
      {upstream.enabled ? (
        <div className="flex items-center gap-3">
          <span className="text-body text-paper" data-testid="upstream-config-enabled-state">
            {t('upstream_detail.maintenance.state.enabled')}
          </span>
          <Button
            size="sm"
            onClick={() => openModal('enter')}
            disabled={pending}
            data-testid="upstream-config-enter-maintenance"
          >
            {t('upstream_detail.maintenance.enter.button')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Chip tone="warn" className="w-fit" testId="upstream-config-maintenance-chip">
            {t('upstream_detail.maintenance.state.disabled')}
          </Chip>
          <Button
            variant="primary"
            className="w-fit"
            onClick={() => openModal('resume')}
            disabled={pending}
            data-testid="upstream-config-resume-routing"
          >
            {t('upstream_detail.maintenance.resume.button')}
          </Button>
        </div>
      )}

      {draftOpen !== false ? (
        <Modal
          isOpen
          onClose={closeModal}
          titleId="upstream-maintenance-modal-title"
          testId="upstream-maintenance-modal"
        >
          <div className="flex flex-col gap-6 p-6">
            <h2
              id="upstream-maintenance-modal-title"
              className="m-0 text-title text-paper"
            >
              {draftOpen === 'enter'
                ? t('upstream_detail.maintenance.modal.title_enter', { name: upstream.name })
                : t('upstream_detail.maintenance.modal.title_resume', { name: upstream.name })}
            </h2>
            <p className="m-0 text-body text-paper-mid">
              {draftOpen === 'enter'
                ? t('upstream_detail.maintenance.modal.body_enter')
                : t('upstream_detail.maintenance.modal.body_resume')}
            </p>
            {draftOpen === 'enter' ? (
              <div
                className="flex flex-col gap-2 rounded-lg bg-well p-4"
                data-testid="upstream-maintenance-impact"
              >
                <span className="text-label text-paper-mid">
                  {t('upstream_detail.maintenance.impact.label', { count: attached.length })}
                </span>
                {attached.length === 0 ? (
                  <p className="m-0 text-dense text-paper-low">
                    {t('upstream_detail.maintenance.impact.none')}
                  </p>
                ) : (
                  <ul className="flex flex-wrap gap-2">
                    {attached.map((m) => (
                      <li
                        key={`${m.vendor}/${m.channel}/${m.model}/${m.protocol}`}
                        className="inline-flex items-center rounded-full border border-line bg-raised px-2.5 py-0.5 font-mono text-micro text-paper"
                      >
                        {m.vendor}/{m.channel}/{m.model} ({m.protocol})
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
            {draftOpen === 'enter' ? (
              <label className="flex flex-col gap-2">
                <span className="text-label text-paper-mid">
                  {t('upstream_detail.maintenance.confirm.type_label')}
                </span>
                <Input
                  type="text"
                  value={typed}
                  onChange={(e) => setTyped(e.currentTarget.value)}
                  placeholder="maintenance"
                  disabled={pending}
                  autoComplete="off"
                  data-testid="upstream-maintenance-typed"
                  mono
                />
              </label>
            ) : null}
            {modalError !== null ? (
              <p
                role="alert"
                data-testid="upstream-maintenance-error"
                className="m-0 text-label text-bad"
              >
                {modalError}
              </p>
            ) : null}
            <div className="flex items-center justify-end gap-3">
              <Button variant="ghost" onClick={closeModal} disabled={pending}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleConfirm()}
                disabled={
                  pending ||
                  (draftOpen === 'enter' && typed.trim() !== 'maintenance')
                }
                loading={pending}
                data-testid="upstream-maintenance-confirm"
              >
                {pending
                  ? t('common.saving')
                  : draftOpen === 'enter'
                  ? t('upstream_detail.maintenance.confirm.submit_enter')
                  : t('upstream_detail.maintenance.confirm.submit_resume')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
